import { Graphics } from 'pixi.js';
import type { RenderContext } from '../context';
import type { Frame } from '../frame';
import { TAU } from '../paint';
import { type Point, drawCable } from './cable';
import {
  CABLE,
  type Cadence,
  WAVE,
  bristlePoint,
  wave,
  wavesBorn,
  windowFill,
  windowOpen,
} from './larsen-moments';

export interface LarsenFloor {
  cable(foot: Point, plug: Point, radius: number, sag: number, alpha: number, frame: Frame): void;
  waves(
    mouth: Point,
    radius: number,
    bornAt: number,
    beats: number,
    still: boolean,
    fade: number,
    frame: Frame,
  ): void;
  ring(at: Point, radius: number, beats: number, alpha: number, frame: Frame): void;
  clear(): void;
  destroy(): void;
}

const RING = { radius: 1.45, y: -0.1, width: 2.4, alpha: 0.6 };
const WAVE_STEPS = 72;

export function createLarsenFloor(ctx: RenderContext, cadence: Cadence): LarsenFloor {
  const floor = ctx.layers.dim.addChild(new Graphics());
  return {
    cable(foot, plug, radius, sag, alpha, frame): void {
      drawCable(floor, foot, plug, radius, { drop: CABLE.drop, sag }, alpha, frame);
    },
    waves(mouth, radius, bornAt, beats, still, fade, frame): void {
      for (const born of wavesBorn(bornAt)) {
        const ring = wave(born, beats, still, windowOpen(born, cadence));
        if (ring === undefined) {
          continue;
        }
        for (let step = 0; step <= WAVE_STEPS; step += 1) {
          const [dx, dy] = bristlePoint(
            step,
            WAVE_STEPS,
            ring.radius * radius,
            ring.bristle * radius,
            ring.turn,
          );
          if (step === 0) {
            floor.moveTo(mouth.x + dx, mouth.y + dy);
          } else {
            floor.lineTo(mouth.x + dx, mouth.y + dy);
          }
        }
        floor.stroke({
          width: ring.gold ? WAVE.goldWidth : WAVE.width,
          color: ring.gold ? frame.palette.noyau : frame.palette.badVibeRim,
          alpha: ring.alpha * fade,
        });
      }
    },
    ring(at, radius, beats, alpha, frame): void {
      const reach = RING.radius * radius;
      const y = at.y + RING.y * radius;
      floor
        .moveTo(at.x, y - reach)
        .arc(at.x, y, reach, -Math.PI / 2, -Math.PI / 2 + TAU * windowFill(beats, cadence))
        .stroke({ width: RING.width, color: frame.palette.or, alpha: RING.alpha * alpha });
    },
    clear(): void {
      floor.clear();
    },
    destroy(): void {
      floor.destroy();
    },
  };
}
